import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// HIMAGOLD — clave usada para recordar que el drawer debe abrirse tras recargar
const HIMAGOLD_OPEN_CART_KEY = 'himagold_open_cart_after_reload';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
      );
    }

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest('product-form-component')
    );

    const quantitySelector = productForm?.refs.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }

      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    const flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.style.setProperty(
      '--start-opacity',
      '0'
    );

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} quantityLabelCartCount
 * @property {HTMLElement | undefined} quantityRules
 * @property {HTMLElement | undefined} productFormButtons
 * @property {HTMLElement | undefined} volumePricing
 * @property {any | undefined} quantitySelector
 * @property {HTMLElement | undefined} quantitySelectorWrapper
 * @property {HTMLElement | undefined} quantityLabel
 * @property {HTMLElement | undefined} pricePerItem
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const target = this.closest(
      '.shopify-section, dialog, product-card'
    );

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#onProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#onCartUpdate,
      { signal }
    );

    /*
     * HIMAGOLD
     *
     * Si venimos de una recarga provocada después de añadir
     * un producto, abrimos automáticamente el drawer.
     */
    this.#restoreCartDrawerAfterReload();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    )?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent =
      document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined(
        'cart-items-component'
      );

      return cartItemsComponent.fetchCartData();
    }

    return fetch(
      `${Theme.routes.cart_url}.json`,
      {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      }
    ).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to fetch cart: ${response.status}`
        );
      }

      return response.json();
    });
  }

  /**
   * ==========================================================
   * HIMAGOLD — RECARGAR Y ABRIR CARRITO
   * ==========================================================
   *
   * Shopify añade correctamente el producto por AJAX,
   * pero el HTML personalizado del drawer no se actualiza
   * correctamente hasta una nueva carga.
   *
   * Guardamos una señal en sessionStorage y refrescamos.
   */
  #reloadAndOpenCart() {
    try {
      sessionStorage.setItem(
        HIMAGOLD_OPEN_CART_KEY,
        'true'
      );
    } catch (error) {
      console.warn(
        '[HIMAGOLD] No se pudo guardar el estado del carrito:',
        error
      );
    }

    window.location.reload();
  }

  /**
   * Después de la recarga, espera a que exista el drawer
   * y lo abre automáticamente.
   */
  #restoreCartDrawerAfterReload() {
    let shouldOpen = false;

    try {
      shouldOpen =
        sessionStorage.getItem(
          HIMAGOLD_OPEN_CART_KEY
        ) === 'true';
    } catch (error) {
      return;
    }

    if (!shouldOpen) return;

    let attempts = 0;
    const maxAttempts = 30;

    const tryOpenDrawer = async () => {
      attempts += 1;

      try {
        await customElements.whenDefined(
          'theme-drawer'
        );
      } catch (error) {
        // Seguimos intentando
      }

      const cartDrawer =
        document.getElementById(
          'cart-drawer'
        );

      if (
        cartDrawer &&
        typeof cartDrawer.open === 'function'
      ) {
        try {
          sessionStorage.removeItem(
            HIMAGOLD_OPEN_CART_KEY
          );
        } catch (error) {
          // No bloqueamos la apertura
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cartDrawer.isOpen) {
              cartDrawer.open();
            }
          });
        });

        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(
          tryOpenDrawer,
          100
        );
      } else {
        console.warn(
          '[HIMAGOLD] No se pudo abrir el drawer después de recargar.'
        );

        try {
          sessionStorage.removeItem(
            HIMAGOLD_OPEN_CART_KEY
          );
        } catch (error) {
          // Nada
        }
      }
    };

    setTimeout(
      tryOpenDrawer,
      100
    );
  }

  /**
   * @param {Cart} cart
   */
  #updateCartQuantity(cart) {
    const variantIdInput =
      this.#getVariantIdInput();

    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) =>
        item.variant_id.toString() ===
        variantIdInput.toString()
    );

    const cartQty =
      cartItem
        ? cartItem.quantity
        : 0;

    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    if (
      quantitySelector?.setCartQuantity
    ) {
      quantitySelector.setCartQuantity(
        cartQty
      );
    }

    this.#updateQuantityLabel(
      cartQty
    );
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        if (
          detail?.sourceId === this.id ||
          detail?.source === 'product-form-component'
        ) {
          return;
        }

        if (detail?.items) {
          this.#updateCartQuantity(
            /** @type {Cart} */ ({
              items: detail.items,
            })
          );
        } else {
          this.#refreshCart().then(
            (cart) =>
              this.#updateCartQuantity(
                cart
              )
          );
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[product-form] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * @param {Event} event
   */
  handleSubmit(event) {
    /*
     * HIMAGOLD
     *
     * Evitamos cualquier submit HTML tradicional.
     * El ADD se hace exclusivamente mediante AJAX.
     */
    event.preventDefault();
    event.stopPropagation();

    if (
      this.#variantChangeInProgress
    ) {
      const intendedVariantId =
        this.#getIntendedVariantId();

      const quantity =
        this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({
          variantId:
            intendedVariantId,

          quantity,
        });
      }

      this.refs
        .addToCartButtonContainer
        ?.animateAddToCart?.();

      return;
    }

    this.#processAddToCart(
      undefined,
      undefined,
      event
    );
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return (
      new URL(
        window.location.href
      ).searchParams.get(
        'variant'
      ) ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return (
      Number(
        this.refs.quantitySelector
          ?.getValue?.()
      ) ||
      Number(
        this.dataset.quantityDefault
      ) ||
      1
    );
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(
    overrideVariantId,
    overrideQuantity,
    event
  ) {
    const {
      addToCartTextError,
    } = this.refs;

    if (this.#timeout) {
      clearTimeout(
        this.#timeout
      );
    }

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    if (!overrideVariantId) {
      const anyButtonDisabled =
        Array.from(
          allAddToCartContainers
        ).some(
          (container) =>
            container.refs
              .addToCartButton
              ?.disabled
        );

      if (anyButtonDisabled) {
        return;
      }
    }

    const form =
      this.querySelector(
        'form'
      );

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }

    if (
      !overrideVariantId &&
      this.refs.quantitySelector
        ?.canAddToCart
    ) {
      const validation =
        this.refs.quantitySelector
          .canAddToCart();

      if (!validation.canAdd) {
        for (
          const container of
          allAddToCartContainers
        ) {
          container.disable();
        }

        const errorTemplate =
          this.dataset
            .quantityErrorMax ||
          '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity
              ?.toString() ||
              ''
          );

        if (addToCartTextError) {
          addToCartTextError
            .classList.remove(
              'hidden'
            );

          const textNode =
            addToCartTextError
              .childNodes[2];

          if (textNode) {
            textNode.textContent =
              errorMessage;
          } else {
            const newTextNode =
              document.createTextNode(
                errorMessage
              );

            addToCartTextError
              .appendChild(
                newTextNode
              );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          if (this.#timeout) {
            clearTimeout(
              this.#timeout
            );
          }

          this.#timeout =
            setTimeout(() => {
              if (
                !addToCartTextError
              ) {
                return;
              }

              addToCartTextError
                .classList.add(
                  'hidden'
                );

              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (
            const container of
            allAddToCartContainers
          ) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData =
      new FormData(form);

    if (overrideVariantId) {
      formData.set(
        'id',
        overrideVariantId
      );
    }

    if (
      overrideQuantity !==
      undefined
    ) {
      formData.set(
        'quantity',
        overrideQuantity.toString()
      );
    }

    /*
     * Section IDs del carrito
     */
    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds =
      [];

    cartItemsComponents.forEach(
      (item) => {
        if (
          item instanceof
            HTMLElement &&
          item.dataset.sectionId
        ) {
          cartItemComponentsSectionIds.push(
            item.dataset.sectionId
          );
        }
      }
    );

    if (
      cartItemComponentsSectionIds
        .length > 0
    ) {
      formData.append(
        'sections',
        cartItemComponentsSectionIds.join(
          ','
        )
      );
    }

    const itemCount =
      Number(
        formData.get(
          'quantity'
        )
      ) ||
      Number(
        this.dataset
          .quantityDefault
      ) ||
      1;

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',

        context: 'product',

        lines: [
          {
            merchandiseId:
              /** @type {string} */ (
                formData.get(
                  'id'
                )
              ),

            quantity:
              itemCount,
          },
        ],

        promise:
          deferredEventPromise.promise,
      })
    );

    /*
     * AJAX ADD TO CART
     */
    fetch(
      Theme.routes.cart_add_url,
      fetchConfig(
        'javascript',
        {
          body: formData,
        }
      )
    )
      .then((response) =>
        response.json()
      )

      .then(async (response) => {
        /*
         * ====================
         * ERROR DE SHOPIFY
         * ====================
         */
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error:
                response.message ||
                'Add to cart failed',

              code: 'INVALID',

              detail: {
                description:
                  response.description,

                errors:
                  response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    ajaxCart
                  ),

                detail: {
                  didError: true,

                  items:
                    ajaxCart.items,

                  source:
                    'product-form-component',

                  sourceId:
                    this.id.toString(),

                  itemCount,

                  productId:
                    this.dataset
                      .productId,
                },
              })
            )

            .catch(
              deferredEventPromise.reject
            );

          if (
            !addToCartTextError
          ) {
            return;
          }

          addToCartTextError
            .classList.remove(
              'hidden'
            );

          const textNode =
            addToCartTextError
              .childNodes[2];

          if (textNode) {
            textNode.textContent =
              response.message;
          } else {
            const newTextNode =
              document.createTextNode(
                response.message
              );

            addToCartTextError
              .appendChild(
                newTextNode
              );
          }

          this.#setLiveRegionText(
            response.message
          );

          this.#timeout =
            setTimeout(() => {
              if (
                !addToCartTextError
              ) {
                return;
              }

              addToCartTextError
                .classList.add(
                  'hidden'
                );

              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        /*
         * ====================
         * ADD CORRECTO
         * ====================
         */

        const id =
          formData.get('id');

        if (
          addToCartTextError
        ) {
          addToCartTextError
            .classList.add(
              'hidden'
            );

          addToCartTextError
            .removeAttribute(
              'aria-live'
            );
        }

        if (!id) {
          throw new Error(
            'Form ID is required'
          );
        }

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (
          anyAddToCartButton
        ) {
          const addedTextElement =
            anyAddToCartButton
              .querySelector(
                '.add-to-cart-text--added'
              );

          const addedText =
            addedTextElement
              ?.textContent
              ?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        /*
         * Comprobamos que el carrito
         * contiene realmente el producto.
         */
        await this.#refreshCart()
          .then((ajaxCart) => {
            deferredEventPromise.resolve({
              cart:
                CartLinesUpdateEvent.createCartFromAjaxResponse(
                  ajaxCart
                ),

              detail: {
                items:
                  ajaxCart.items,

                source:
                  'product-form-component',

                sourceId:
                  this.id.toString(),

                itemCount,

                productId:
                  this.dataset
                    .productId,

                sections:
                  response.sections,

                didError:
                  false,
              },
            });

            if (
              this.#getVariantIdInput()
            ) {
              this.#updateCartQuantity(
                ajaxCart
              );
            }

            return ajaxCart;
          })

          .catch(
            deferredEventPromise.reject
          );

        /*
         * ==============================================
         * HIMAGOLD — SOLUCIÓN FINAL DEL DRAWER
         * ==============================================
         *
         * El producto YA está en Shopify.
         *
         * Recargamos la misma página para que
         * Liquid vuelva a generar el drawer
         * actualizado y después lo abrimos
         * automáticamente.
         */
        this.#reloadAndOpenCart();
      })

      .catch((error) => {
        console.error(
          error
        );

        deferredEventPromise.reject(
          error
        );

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              error?.message ||
              'Network error during add to cart',

            code:
              'SERVICE_UNAVAILABLE',
          })
        );
      })

      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent(
            'add:user-action',
            event
          );
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   */
  #processBatchAddToCart(
    items
  ) {
    if (
      items.length === 0
    ) {
      return;
    }

    const {
      addToCartTextError,
    } = this.refs;

    if (this.#timeout) {
      clearTimeout(
        this.#timeout
      );
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds =
      [];

    for (
      const item of
      cartItemsComponents
    ) {
      if (
        item instanceof
          HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }
    }

    const totalQuantity =
      items.reduce(
        (sum, item) =>
          sum +
          item.quantity,
        0
      );

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',

        context: 'product',

        lines:
          items.map(
            (item) => ({
              merchandiseId:
                item.variantId,

              quantity:
                item.quantity,
            })
          ),

        promise:
          deferredEventPromise.promise,
      })
    );

    /** @type {{items: Array<{id: number, quantity: number}>, sections?: string}} */
    const payload = {
      items:
        items.map(
          (item) => ({
            id:
              Number(
                item.variantId
              ),

            quantity:
              item.quantity,
          })
        ),
    };

    if (
      cartItemComponentsSectionIds
        .length > 0
    ) {
      payload.sections =
        cartItemComponentsSectionIds.join(
          ','
        );
    }

    fetch(
      Theme.routes.cart_add_url,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json',

          'X-Requested-With':
            'XMLHttpRequest',
        },

        body:
          JSON.stringify(
            payload
          ),
      }
    )

      .then((response) =>
        response.json()
      )

      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error:
                response.message ||
                'Add to cart failed',

              code:
                'INVALID',

              detail: {
                description:
                  response.description,

                errors:
                  response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    ajaxCart
                  ),

                detail: {
                  didError:
                    true,

                  items:
                    ajaxCart.items,

                  source:
                    'product-form-component',

                  sourceId:
                    this.id.toString(),

                  itemCount:
                    totalQuantity,

                  productId:
                    this.dataset
                      .productId,
                },
              })
            )

            .catch(
              deferredEventPromise.reject
            );

          if (
            !addToCartTextError
          ) {
            return;
          }

          addToCartTextError
            .classList.remove(
              'hidden'
            );

          const textNode =
            addToCartTextError
              .childNodes[2];

          if (textNode) {
            textNode.textContent =
              response.message;
          } else {
            addToCartTextError
              .appendChild(
                document.createTextNode(
                  response.message
                )
              );
          }

          this.#setLiveRegionText(
            response.message
          );

          this.#timeout =
            setTimeout(() => {
              addToCartTextError
                .classList.add(
                  'hidden'
                );

              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (
          addToCartTextError
        ) {
          addToCartTextError
            .classList.add(
              'hidden'
            );

          addToCartTextError
            .removeAttribute(
              'aria-live'
            );
        }

        const allAddToCartContainers =
          /** @type {NodeListOf<AddToCartComponent>} */ (
            this.querySelectorAll(
              'add-to-cart-component'
            )
          );

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (
          anyAddToCartButton
        ) {
          const addedTextElement =
            anyAddToCartButton
              .querySelector(
                '.add-to-cart-text--added'
              );

          const addedText =
            addedTextElement
              ?.textContent
              ?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(
            () =>
              this.#clearLiveRegionText(),
            SUCCESS_MESSAGE_DISPLAY_DURATION
          );
        }

        const cart =
          await this.#refreshCart();

        deferredEventPromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              cart
            ),

          detail: {
            items:
              cart.items,

            source:
              'product-form-component',

            sourceId:
              this.id.toString(),

            itemCount:
              totalQuantity,

            productId:
              this.dataset
                .productId,

            sections:
              response.sections,

            didError:
              false,
          },
        });

        this.#updateCartQuantity(
          cart
        );

        /*
         * HIMAGOLD
         *
         * Mismo comportamiento si el añadido
         * se realiza mediante una cola/lote.
         */
        this.#reloadAndOpenCart();
      })

      .catch((error) => {
        console.error(
          error
        );

        deferredEventPromise.reject(
          error
        );

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              error?.message ||
              'Network error during add to cart',

            code:
              'SERVICE_UNAVAILABLE',
          })
        );
      });
  }

  /**
   * @param {number} cartQty
   */
  #updateQuantityLabel(
    cartQty
  ) {
    const quantityLabel =
      this.refs
        .quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel
          .textContent
          ?.match(
            /\((\d+)\s+(.+)\)/
          );

      if (
        inCartText &&
        inCartText[2]
      ) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(
    text
  ) {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      text;
  }

  #clearLiveRegionText() {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      '';
  }

  /**
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );
    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();
    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement
        .insertAdjacentElement(
          'beforebegin',

          /** @type {Element} */ (
            newElement.cloneNode(
              true
            )
          )
        );
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (
    event
  ) => {
    const sourceCard =
      /** @type {Element | null} */ (
        event.target
      )?.closest(
        'product-card'
      );

    if (
      sourceCard &&
      !sourceCard.contains(
        this
      )
    ) {
      return;
    }

    const generation =
      ++this.#variantChangeGeneration;

    this.#variantChangeInProgress =
      true;

    try {
      const { detail } =
        await event.promise;

      if (!detail?.html) {
        return;
      }

      const {
        html,
        newProduct,
        productId,
        resource,
      } = detail;

      if (newProduct) {
        this.dataset.productId =
          newProduct.id;
      } else if (
        productId &&
        productId !==
          this.dataset.productId
      ) {
        return;
      }

      const {
        variantId,
      } = this.refs;

      variantId.value =
        resource?.id ?? '';

      const {
        addToCartButtonContainer:
          currentAddToCartButtonContainer,

        acceleratedCheckoutButtonContainer,
      } = this.refs;

      const currentAddToCartButton =
        currentAddToCartButtonContainer
          ?.refs.addToCartButton;

      if (
        !currentAddToCartButtonContainer ||
        (
          !currentAddToCartButton &&
          !acceleratedCheckoutButtonContainer
        )
      ) {
        return;
      }

      if (
        resource == null ||
        resource.available ==
          false
      ) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton =
        html.querySelector(
          'product-form-component [ref="addToCartButton"]'
        );

      if (
        newAddToCartButton &&
        currentAddToCartButton
      ) {
        morph(
          currentAddToCartButton,
          newAddToCartButton
        );
      }

      if (
        acceleratedCheckoutButtonContainer
      ) {
        if (
          resource == null ||
          resource.available ==
            false
        ) {
          acceleratedCheckoutButtonContainer
            .setAttribute(
              'hidden',
              'true'
            );
        } else {
          acceleratedCheckoutButtonContainer
            .removeAttribute(
              'hidden'
            );
        }
      }

      if (resource) {
        const productVariantMedia =
          resource.featured_media
            ?.preview_image
            ?.src;

        if (
          productVariantMedia
        ) {
          this.refs
            .addToCartButtonContainer
            ?.setAttribute(
              'data-product-variant-media',
              productVariantMedia +
                '&width=100'
            );
        }
      }

      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      const newQuantityInput =
        /** @type {HTMLInputElement | null} */ (
          html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );

      if (
        quantitySelector
          ?.updateConstraints &&
        newQuantityInput
      ) {
        quantitySelector
          .updateConstraints(
            newQuantityInput.min,
            newQuantityInput.max ||
              null,
            newQuantityInput.step
          );

        this.dataset.quantityDefault =
          newQuantityInput.min ||
          '1';
      }

      const newQuantityRules =
        html.querySelector(
          '.quantity-rules'
        );

      const isQuantityRulesChanging =
        !!quantityRules !==
        !!newQuantityRules;

      const newPricePerItem =
        html.querySelector(
          'price-per-item'
        );

      const isPricePerItemChanging =
        !!pricePerItem !==
        !!newPricePerItem;

      if (
        (
          isQuantityRulesChanging ||
          isPricePerItemChanging
        ) &&
        quantitySelector
      ) {
        const currentQuantityValue =
          quantitySelector
            .getValue?.();

        const newProductFormButtons =
          html.querySelector(
            '.product-form-buttons'
          );

        if (
          productFormButtons &&
          newProductFormButtons
        ) {
          morph(
            productFormButtons,
            newProductFormButtons
          );

          const newQuantityInputElement =
            /** @type {HTMLInputElement | null} */ (
              html.querySelector(
                'quantity-selector-component input[ref="quantityInput"]'
              )
            );

          if (
            this.refs.quantitySelector
              ?.updateConstraints &&
            newQuantityInputElement &&
            currentQuantityValue
          ) {
            this.refs.quantitySelector
              .setValue(
                currentQuantityValue
              );

            this.refs.quantitySelector
              .updateConstraints(
                newQuantityInputElement.min,

                newQuantityInputElement.max ||
                  null,

                newQuantityInputElement.step
              );

            this.dataset.quantityDefault =
              newQuantityInputElement.min ||
              '1';
          }
        }
      } else {
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          [
            '.quantity-label',
            quantityLabel,
            quantitySelector,
          ],

          [
            '.quantity-rules',
            quantityRules,
            this.refs
              .productFormButtons,
          ],

          [
            'price-per-item',
            pricePerItem,
            quantitySelectorWrapper,
          ],
        ];

        for (
          const [
            selector,
            currentElement,
            fallback,
          ] of morphTargets
        ) {
          this.#morphOrUpdateElement(
            currentElement,

            html.querySelector(
              selector
            ),

            fallback
          );
        }
      }

      const currentVolumePricing =
        this.refs
          .volumePricing;

      const newVolumePricing =
        html.querySelector(
          'volume-pricing'
        );

      this.#morphOrUpdateElement(
        currentVolumePricing,
        newVolumePricing,
        this.refs.productFormButtons
      );

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) {
        return;
      }

      this.#refreshCart()
        .then(
          (cart) =>
            this.#updateCartQuantity(
              cart
            )
        );
    } finally {
      if (
        generation ===
        this.#variantChangeGeneration
      ) {
        this.#variantChangeInProgress =
          false;

        if (
          this.#addToCartQueue
            .length > 0
        ) {
          const queuedItems = [
            ...this.#addToCartQueue,
          ];

          this.#addToCartQueue =
            [];

          this.#processBatchAddToCart(
            queuedItems
          );
        }
      }
    }
  };
}

if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}